import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField as MuiTextField,
  Typography,
  IconButton,
} from '@mui/material';
import { ListLive } from '@react-admin/ra-realtime';
import CloseIcon from '@mui/icons-material/Close';
import { Datagrid } from '@react-admin/ra-rbac';
import {
  DateField,
  FunctionField,
  Pagination,
  ReferenceField,
  TextField,
  TopToolbar,
  useGetIdentity,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import authProvider from '../../../utils/authProvider';
import { typedHttpClient } from '../../../utils/httpClient';
import { LIST_DEBOUNCE } from '../../../config';
import { DateFieldWithTime } from '../../../components/fields/DateFieldWithTime';

type AccessReview = {
  type: 'approved' | 'rejected';
  forced?: boolean;
  reason?: string;
} | null;

type RelationRecord = {
  id: string;
  _id: string;
  createdAt: string;
  user: { _id: string; _ref: string };
  customer: { _id: string; _ref: string };
  roles: string[];
  accessRequest?: {
    validUntil: string;
    reason: string;
    review: AccessReview;
    forced?: boolean;
  };
};

type ReviewPageActionsContextValue = {
  busyId: string | null;
  openReject: (id: string) => void;
  approve: (id: string, accessRequest?: RelationRecord['accessRequest']) => Promise<void>;
};

const ReviewPageActionsContext = createContext<ReviewPageActionsContextValue | null>(null);

const useReviewPageActions = () => {

  const ctx = useContext(ReviewPageActionsContext);
  if (!ctx) {

    throw new Error('ReviewPageActionsContext missing');

  }
  return ctx;

};

const reviewStatusLabel = (
  record: RelationRecord,
  translate: ReturnType<typeof useTranslate>,
) => {

  const review = record?.accessRequest?.review;
  if (review == null) {

    return translate('resources.customer_access_review.status.open');

  }
  if (review.type === 'approved') {

    return translate('resources.customer_access_review.status.approved');

  }
  return translate('resources.customer_access_review.status.rejected');

};

const AccessReviewListToolbar = ({
  showAllRequests,
  onShowAllChange,
}: {
  showAllRequests: boolean;
  onShowAllChange: (value: boolean) => void;
}) => {

  const translate = useTranslate();
  return (
    <TopToolbar>
      <Box sx={{ flex: '1 1 auto' }}/>
      <FormControlLabel
        control={(
          <Switch
            checked={showAllRequests}
            onChange={(_, checked) => onShowAllChange(checked)}
            color="primary"
            size="small"
          />
        )}
        label={translate('resources.customer_access_review.switch.show_all')}
        labelPlacement="start"
        sx={{ mr: 0, ml: 1 }}
      />
    </TopToolbar>
  );

};

const ReviewActionsInner = ({ record }: { record: RelationRecord }) => {

  const { data: identity } = useGetIdentity();
  const translate = useTranslate();
  const { busyId, openReject, approve } = useReviewPageActions();

  const open = record?.accessRequest?.review == null;
  const own = identity?.id === record?.user?._id;
  if (!open) {

    return null;

  }
  if (own) {

    return (
      <Typography variant="caption" color="text.secondary">
        {translate('resources.customer_access_review.text.cannot_review_own_request')}
      </Typography>
    );

  }
  return (
    <>
      <Button
        size="small"
        color="primary"
        disabled={busyId === record.id}
        onClick={() => approve(record.id, record.accessRequest)}
      >
        {translate('resources.customer_access_review.actions.approve')}
      </Button>
      <Button
        size="small"
        color="secondary"
        disabled={busyId === record.id}
        onClick={() => openReject(record.id)}
        sx={{ ml: 1 }}
      >
        {translate('resources.customer_access_review.actions.reject')}
      </Button>
    </>
  );

};

export const CustomerAccessRequestReviewPage = () => {

  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const refresh = useRefresh();
  const { isLoading: identityLoading } = useGetIdentity();

  const globalRoles = useMemo(() => {

    try {

      const ctx = JSON.parse(localStorage.getItem('context') || '{}');
      return ctx.user?.globalRoles || [];

    } catch {

      return [];

    }

  }, []);

  const roles = authProvider.getRoles();
  const canAccess = roles.includes('admin')
    || authProvider.isDeveloperAdmin()
    || globalRoles.includes('support_global');

  const canToggleShowAll = authProvider.isAdmin() || authProvider.isDeveloperAdmin();
  const [showAllRequests, setShowAllRequests] = useState(false);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const openReject = useCallback((id: string) => {

    setRejectId(id);
    setRejectReason('');
    setRejectOpen(true);

  }, []);

  const approve = useCallback(async (id: string, accessRequest?: RelationRecord['accessRequest']) => {

    setBusyId(id);
    try {

      const forcedReq = Boolean((accessRequest as { forced?: boolean } | undefined)?.forced);
      const reviewPayload = forcedReq
        ? { review: { type: 'approved' as const, forced: true } }
        : { review: { type: 'approved' as const } };
      await typedHttpClient().customerUserRelations.customerUserRelationsControllerReviewAccessRequest(
        id,
        reviewPayload as never,
      );
      notify('resources.customer_access_review.notifications.approve_success', { type: 'success' });
      refresh();

    } catch (e: unknown) {

      const msg = e && typeof e === 'object' && 'message' in e ? String((e as Error).message) : 'ra.notification.http_error';
      notify(msg, { type: 'warning' });

    } finally {

      setBusyId(null);

    }

  }, [notify, refresh]);

  const confirmReject = async () => {

    if (!rejectId) {

      return;

    }
    const trimmed = rejectReason.trim();
    if (trimmed.length < 10) {

      notify('resources.customer_access_review.validation.reject_reason_min_length', { type: 'warning' });
      return;

    }
    setBusyId(rejectId);
    try {

      await typedHttpClient().customerUserRelations.customerUserRelationsControllerReviewAccessRequest(
        rejectId,
        { review: { type: 'rejected', reason: trimmed } },
      );
      notify('resources.customer_access_review.notifications.reject_success', { type: 'success' });
      setRejectOpen(false);
      setRejectId(null);
      refresh();

    } catch (e: unknown) {

      const msg = e && typeof e === 'object' && 'message' in e ? String((e as Error).message) : 'ra.notification.http_error';
      notify(msg, { type: 'warning' });

    } finally {

      setBusyId(null);

    }

  };

  const actionsContextValue = useMemo(() => ({
    busyId,
    openReject,
    approve,
  }), [busyId, openReject, approve]);

  const listQueryOptions = useMemo(() => ({
    meta: {
      accessRequestReviewList: true,
      accessRequestReviewIncludeClosed: showAllRequests,
    },
  }), [showAllRequests]);

  const listActions = useMemo(() => {

    if (!canToggleShowAll) {

      return undefined;

    }
    return (
      <AccessReviewListToolbar
        showAllRequests={showAllRequests}
        onShowAllChange={setShowAllRequests}
      />
    );

  }, [canToggleShowAll, showAllRequests]);

  useEffect(() => {

    if (!identityLoading && !canAccess) {

      notify('resources.config.text.no_access', { type: 'warning' });
      navigate('/');

    }

  }, [canAccess, identityLoading, navigate, notify]);

  if (!canAccess) {

    return null;

  }

  return (
    <ReviewPageActionsContext.Provider value={actionsContextValue}>
      <ListLive
        key={showAllRequests ? 'all' : 'open'}
        resource="customer-user-relations"
        title={'resources.customer_access_review.title'}
        debounce={LIST_DEBOUNCE}
        sort={{ field: 'updatedAt', order: 'DESC' }}
        perPage={25}
        pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
        queryOptions={listQueryOptions}
        actions={listActions}
        exporter={false}
      >
        <Datagrid rowClick={false} bulkActionButtons={false}>
          <ReferenceField source="customer._id" reference="customers" label="resources.customer_access_review.fields.customer"/>
          <ReferenceField source="user._id" reference="users" label="resources.customer_access_review.fields.requested_by"/>
          <FunctionField
            label="resources.customer_access_review.fields.requested_role"
            render={(record: RelationRecord) => {

              return (record.roles || []).join(', ');

            }}
          />
          <DateField source="accessRequest.validUntil" label="resources.customer_access_review.fields.valid_until" showTime/>
          <TextField source="accessRequest.reason" label="resources.customer_access_review.fields.reason"/>
          {showAllRequests && (
            <FunctionField
              label="resources.customer_access_review.fields.force_requested"
              render={(record: RelationRecord) => {

                const forcedReq = Boolean((record.accessRequest as { forced?: boolean } | undefined)?.forced);
                return forcedReq
                  ? translate('resources.customer_access_review.forced.yes')
                  : translate('resources.customer_access_review.forced.no');

              }}
            />
          )}
          {showAllRequests && (
            <>
              <FunctionField
                label="resources.customer_access_review.fields.status"
                render={(record: RelationRecord) => reviewStatusLabel(record, translate)}
              />
            </>
          )}
          <DateFieldWithTime source="createdAt" label="general.fields.createdAt" timeOnHover={true}/>
          <FunctionField
            label="resources.customer_access_review.fields.column_actions"
            render={(record: RelationRecord) => <ReviewActionsInner record={record}/>}
          />
        </Datagrid>
      </ListLive>

      <Dialog open={rejectOpen} onClose={() => !busyId && setRejectOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle className="flex-in-between"><span>{translate('resources.customer_access_review.actions.reject')}</span>
          <IconButton onClick={() => setRejectOpen(false)}>
            <CloseIcon/>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <MuiTextField
            autoFocus
            margin="dense"
            label={translate('resources.customer_access_review.actions.reject_reason')}
            fullWidth
            multiline
            minRows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={!!busyId}>{translate('ra.action.cancel')}</Button>
          <Button onClick={confirmReject} color="primary" variant="contained" disabled={!!busyId}>
            {translate('resources.customer_access_review.actions.reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </ReviewPageActionsContext.Provider>
  );

};
